import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiUsers, FiShield, FiArrowRight, FiStar, FiAward } = FiIcons;

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
      title: 'Professionelles Facility Management',
      subtitle: 'Wir sorgen dafür, dass Ihre Immobilien optimal funktionieren.'
    },
    {
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
      title: 'Reinigungsmanagement',
      subtitle: 'Höchste Standards für saubere und gepflegte Räumlichkeiten.'
    },
    {
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
      title: 'Professionelles Reinigungsmanagement',
      subtitle: 'Professionelle Reinigungsdienste für alle Arten von Gebäuden und Anlagen.'
    }
  ];

  const services = [
    {
      icon: FiCheckCircle,
      title: 'Reinigungsmanagement',
      description: 'Professionelle Reinigungsdienste für alle Arten von Gebäuden und Anlagen.',
      features: [
        'Büro- und Geschäftsreinigung',
        'Industriereinigung',
        'Glasreinigung',
        'Teppichreinigung',
        'Spezialreinigung',
        'Desinfektion'
      ]
    }
  ];

  const stats = [
    { number: '500+', label: 'Zufriedene Kunden', icon: FiUsers },
    { number: '15+', label: 'Jahre Erfahrung', icon: FiAward },
    { number: '100%', label: 'Zertifizierte Reinigung', icon: FiShield },
    { number: '99%', label: 'Kundenzufriedenheit', icon: FiStar }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  return (
    <div>
      {/* Hero Slider */}
      <section className="relative h-screen overflow-hidden">
        {heroSlides.map((slide, index) => (
          <motion.div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.4),rgba(0,0,0,0.4)), url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <div className="text-center max-w-4xl px-4 sm:px-6 lg:px-8">
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4"
                >
                  {slide.title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-orange-400 text-xl sm:text-2xl md:text-3xl font-semibold mb-6"
                >
                  Wo andere aufhören, fangen wir an
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-200"
                >
                  {slide.subtitle}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center"
                >
                  <Link
                    to="/services"
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center space-x-2"
                  >
                    <span>Unsere Leistungen</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
                  >
                    Kontakt aufnehmen
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        ))}

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-200 ${index === currentSlide ? 'bg-orange-500' : 'bg-white bg-opacity-50'}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center relative p-6 bg-gray-50 rounded-xl"
              >
                <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-700">
                  Demo-Daten
                </span>
                <SafeIcon icon={stat.icon} className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 mx-auto mb-4" />
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 text-sm sm:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Beautiful Single-Column Layout */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Unsere Leistungen</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Wir bieten umfassende Facility Services Lösungen, die auf Ihre individuellen Bedürfnisse zugeschnitten sind.
            </p>
          </div>
          <div className="flex justify-center max-w-4xl mx-auto w-full">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 hover:shadow-2xl transition-all duration-300 w-full text-center border border-gray-100 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 to-orange-600"></div>
                <SafeIcon icon={service.icon} className="w-16 h-16 sm:w-20 sm:h-20 text-orange-500 mx-auto mb-8" />
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                  {service.title}
                </h3>
                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                  {service.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto mb-10">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700 bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0" />
                      <span className="font-medium text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center bg-orange-50 hover:bg-orange-100 text-orange-600 font-semibold px-8 py-4 rounded-xl transition-colors duration-200"
                >
                  <span>Alle Details ansehen</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Bereit für professionelle Hubler & Tex Facility Services?
            </h2>
            <p className="text-lg sm:text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch. Wir entwickeln gemeinsam die optimale Lösung für Ihre Immobilie.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center space-x-2"
              >
                <span>Jetzt Beratung anfragen</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <Link
                to="/portal"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-500 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Kundenportal
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;